/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/map";
import _concatInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/concat";
import _Array$isArray from "@babel/runtime-corejs3/core-js-stable/array/is-array";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/for-each";
import _Map from "@babel/runtime-corejs3/core-js-stable/map";
import _someInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/some";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _reduceInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/reduce";
import _createForOfIteratorHelper from "@babel/runtime-corejs3/helpers/esm/createForOfIteratorHelper";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/classes/input-plugin-manager */
// TODO: SET PLUGINS OBJECTS DEFAULTS!
import { arrayUtils, objectUtils } from '@codistica/core';
import { InputValidatorPluginUtils } from './input-validator-plugin-utils.js';

/**
 * @callback inputBlockerInstanceType
 * @param {Object<string,*>} e - Input event object.
 * @returns {boolean} - Should block.
 */

/**
 * @typedef inputBlockerType
 * @property {'blocker'} type - Plugin type.
 * @property {string} name - Plugin name.
 * @property {inputBlockerInstanceType} plugin - Blocker instance.
 */

/**
 * @callback inputFilterInstanceType
 * @param {string} value - Input value.
 * @returns {string} - Filtered output.
 */

/**
 * @typedef inputFilterType
 * @property {'filter'} type - Plugin type.
 * @property {string} name - Plugin name.
 * @property {inputFilterInstanceType} plugin - Filter instance.
 */

/**
 * @typedef {(string|RegExp|function(string): Object<string,*>)} inputValidatorInstanceType
 */

/**
 * @typedef inputValidatorType
 * @property {'validator'} type - Plugin type.
 * @property {string} name - Plugin name.
 * @property {string} [groupName] - Plugin group name.
 * @property {Array<Object<string,*>>} [groupMessages] - Plugin group error messages.
 * @property {Array<Object<string,*>>} [messages] - Plugin error messages.
 * @property {inputValidatorInstanceType} plugin - Validator instance.
 */

/**
 * @typedef {pluginType} inputPresetInstanceType
 */

/**
 * @typedef inputPresetType
 * @property {'preset'} type - Plugin type.
 * @property {string} name - Plugin name.
 * @property {Array<Object<string,*>>} groupMessages - Plugin group error messages.
 * @property {inputPresetInstanceType} plugin - Preset instance.
 */

/**
 * @typedef {(inputBlockerType|inputFilterType|inputValidatorType|inputPresetType)} allPluginsType
 */

/**
 * @callback pluginWrapperType
 * @param {*} [options] - Plugin options.
 * @returns {allPluginsType} - Plugin.
 */

/**
 * @typedef {((AllPluginsType|PluginWrapperType)|Array<(AllPluginsType|PluginWrapperType)>)} pluginType
 */

/**
 * @typedef runValidatorsOutputType
 * @property {(boolean|null)} result - Validation result.
 * @property {Object<string,Object<string,(boolean|null)>>} report - Validation report.
 * @property {Array<Object<string,*>>} messages - Validation error messages.
 * @property {Object<string,Object<string,*>>} data - Validation data.
 */

/**
 * @classdesc A class for input plugin management.
 */
var InputPluginManager = /*#__PURE__*/function () {
  /**
   * @description Constructor.
   */
  function InputPluginManager() {
    var _context, _context2, _context3, _context4;

    _classCallCheck(this, InputPluginManager);

    _defineProperty(this, "blockers", void 0);

    _defineProperty(this, "filters", void 0);

    _defineProperty(this, "validators", void 0);

    this.blockers = [];
    this.filters = [];
    this.validators = []; // BIND METHODS

    this.runValidators = _bindInstanceProperty(_context = this.runValidators).call(_context, this);
    this.runFilters = _bindInstanceProperty(_context2 = this.runFilters).call(_context2, this);
    this.runBlockers = _bindInstanceProperty(_context3 = this.runBlockers).call(_context3, this);
    this.loadPlugins = _bindInstanceProperty(_context4 = this.loadPlugins).call(_context4, this);
  }
  /**
   * @instance
   * @description Validates input value using loaded validators.
   * @param {string} value - Value to be validated.
   * @returns {runValidatorsOutputType} Result.
   */


  _createClass(InputPluginManager, [{
    key: "runValidators",
    value: function runValidators(value) {
      var output = {};

      var _iterator = _createForOfIteratorHelper(this.validators),
          _step;

      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var validator = _step.value;
          var validatorOutput = getNormalizedValidatorOutput(value, validator);
          output[validator.name] = validatorOutput;

          if (validatorOutput.result === false) {
            break;
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }

      return output;
    }
    /**
     * @instance
     * @description Filters input using loaded filters.
     * @param {string} value - Value to be filtered.
     * @returns {string} Filtered value.
     */

  }, {
    key: "runFilters",
    value: function runFilters(value) {
      var _context5,
          _this = this,
          _context6;

      return _reduceInstanceProperty(_context5 = this.filters).call(_context5, _bindInstanceProperty(_context6 = function _context6(acc, filter) {
        _newArrowCheck(this, _this);

        return filter.plugin(acc);
      }).call(_context6, this), value);
    }
    /**
     * @instance
     * @description Indicates if event should be blocked using loaded blockers.
     * @param {Object<string,*>} e - Event object.
     * @returns {boolean} Result.
     */

  }, {
    key: "runBlockers",
    value: function runBlockers(e) {
      var _context7,
          _this2 = this,
          _context8;

      var isPrintable = e.key && e.key.length === 1;
      return _someInstanceProperty(_context7 = this.blockers).call(_context7, _bindInstanceProperty(_context8 = function _context8(blocker) {
        _newArrowCheck(this, _this2);

        return !!(isPrintable && blocker.plugin(e));
      }).call(_context8, this));
    }
    /**
     * @instance
     * @description Load plugins into instance.
     * @param {pluginType} plugins - Plugins to be loaded.
     */

  }, {
    key: "loadPlugins",
    value: function loadPlugins(plugins) {
      var _loadPlugins2 = _loadPlugins(plugins),
          blockers = _loadPlugins2.blockers,
          filters = _loadPlugins2.filters,
          validators = _loadPlugins2.validators;

      this.blockers = mergePlugins(this.blockers, blockers);
      this.filters = mergePlugins(this.filters, filters);
      this.validators = mergePlugins(this.validators, validators);
    }
    /**
     * @description Initializes plugin if not already initialized.
     * @param {(AllPluginsType|PluginWrapperType)} plugin - Plugin to be initialized.
     * @returns {(AllPluginsType|null)} Initialized plugin.
     */

  }], [{
    key: "initializePlugin",
    value: function initializePlugin(plugin) {
      if (typeof plugin === 'function') {
        var returnedValue = plugin();

        if (objectUtils.isPureObject(returnedValue) && returnedValue.type) {
          return returnedValue;
        }
      } else if (objectUtils.isPureObject(plugin) && plugin.type) {
        return plugin;
      }

      return null;
    }
  }]);

  return InputPluginManager;
}();
/**
 * @description Plugins merge utility function.
 * @param {...AllPluginsType} pluginsArg - Plugins to be loaded.
 * @returns {Array<AllPluginsType>} Merged plugins.
 */


function mergePlugins() {
  var _this3 = this,
      _context9;

  var output = [];
  var outputMap = new _Map();

  for (var _len = arguments.length, pluginsArg = new Array(_len), _key = 0; _key < _len; _key++) {
    pluginsArg[_key] = arguments[_key];
  }

  _forEachInstanceProperty(pluginsArg).call(pluginsArg, _bindInstanceProperty(_context9 = function _context9(pluginArg) {
    var _this4 = this;

    _newArrowCheck(this, _this3);

    if (_Array$isArray(pluginArg)) {
      var _context10;

      _forEachInstanceProperty(pluginArg).call(pluginArg, _bindInstanceProperty(_context10 = function _context10(plugin) {
        _newArrowCheck(this, _this4);

        var index = outputMap.get(plugin.name);

        if (typeof index !== 'number') {
          // ADD PLUGIN
          outputMap.set(plugin.name, output.length);
          output.push(plugin);
        } else {
          // REPLACE PLUGIN
          output[index] = plugin;
        }
      }).call(_context10, this));
    } else {
      var plugin = pluginArg;
      var index = outputMap.get(plugin.name);

      if (typeof index !== 'number') {
        // ADD PLUGIN
        outputMap.set(plugin.name, output.length);
        output.push(plugin);
      } else {
        // REPLACE PLUGIN
        output[index] = plugin;
      }
    }
  }).call(_context9, this));

  return output;
}
/**
 * @description Scans and initializes plugins.
 * @param {pluginType} plugins - Plugins to be loaded.
 * @returns {{blockers: Array<BlockerType>, filters: Array<FilterType>, validators: Array<ValidatorType>}} Plugins object.
 */


function _loadPlugins(plugins) {
  var _context11,
      _this5 = this,
      _context12;

  var output = {
    blockers: [],
    filters: [],
    validators: []
  };

  _forEachInstanceProperty(_context11 = arrayUtils.flatten(arrayUtils.normalize(plugins))).call(_context11, _bindInstanceProperty(_context12 = function _context12(plugin) {
    var _context13,
        _context14,
        _context15,
        _context16,
        _this6 = this,
        _context17;

    _newArrowCheck(this, _this5);

    var pluginObject = InputPluginManager.initializePlugin(plugin);

    if (!pluginObject) {
      return;
    }

    var presetOutput = null;

    switch (pluginObject.type) {
      case 'blocker':
        output.blockers.push(pluginObject);
        break;

      case 'filter':
        output.filters.push(pluginObject);
        break;

      case 'validator':
        output.validators.push(pluginObject);
        break;

      case 'preset':
        presetOutput = _loadPlugins(pluginObject.plugin);
        output.blockers = _concatInstanceProperty(_context13 = output.blockers).call(_context13, presetOutput.blockers);
        output.filters = _concatInstanceProperty(_context14 = output.filters).call(_context14, presetOutput.filters);
        output.validators = _concatInstanceProperty(_context15 = output.validators).call(_context15, _mapInstanceProperty(_context16 = presetOutput.validators).call(_context16, _bindInstanceProperty(_context17 = function _context17(validator) {
          _newArrowCheck(this, _this6);

          validator.groupName = pluginObject.name;
          validator.groupErrorMessages = pluginObject.groupErrorMessages;
          return validator;
        }).call(_context17, this)));
        break;

      default:
        break;
    }
  }).call(_context12, this));

  return output;
}
/**
 * @description Calls validator for specified value and returns a normalized output.
 * @param {string} value - Value to be validated.
 * @param {inputValidatorType} validator - Validator instance to be used.
 * @returns {Object<string,*>} Validator output.
 */


function getNormalizedValidatorOutput(value, validator) {
  var validatorOutput = {
    result: true,
    report: {},
    messages: {},
    data: {},
    promises: {}
  };

  if (typeof validator.plugin === 'function') {
    var fnOutput = validator.plugin(value);

    if (typeof fnOutput === 'boolean' || fnOutput === null) {
      validatorOutput.result = fnOutput;
    } else {
      validatorOutput = fnOutput;
    }
  } else if (typeof validator.plugin === 'string') {
    validatorOutput.result = validator.plugin === value;
  } else if (validator.plugin instanceof RegExp) {
    validatorOutput.result = validator.plugin.test(value);
  }

  if (validatorOutput.result === false) {
    // ADD PLUGIN OBJECT ERROR MESSAGES
    if (validator.errorMessages) {
      for (var i in validator.errorMessages) {
        if (!Object.hasOwnProperty.call(validator.errorMessages, i)) {
          continue;
        }

        if (validator.errorMessages[i]) {
          validatorOutput.messages[i] = InputValidatorPluginUtils.createMessageObject(validator.errorMessages[i], undefined, {
            sortKey: -1
          });
        }
      }
    } // ADD PLUGIN OBJECT GROUP ERROR MESSAGES


    if (validator.groupErrorMessages) {
      for (var _i in validator.groupErrorMessages) {
        if (!Object.hasOwnProperty.call(validator.groupErrorMessages, _i)) {
          continue;
        }

        if (validator.groupErrorMessages[_i]) {
          validatorOutput.messages[_i] = InputValidatorPluginUtils.createMessageObject(validator.groupErrorMessages[_i], undefined, {
            sortKey: -1
          });
        }
      }
    }
  }

  return validatorOutput;
}

export { InputPluginManager };